import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportCategory } from '../../../models/Support/ISupportCategory';
import {
	useCreateCategoryMutation,
	useDeleteCategoryMutation,
	useSupportCategoriesQuery,
	useUpdateCategoryMutation,
} from '../../../queries/Support/useSupportCategories';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { ColorPicker } from '../../../web/components-v2/ColorPicker';
import { Input } from '../../../web/components-v2/Input';
import { TextArea } from '../../../web/components-v2/TextArea';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { PlusIcon } from './SettingsIcons';
import { categoriesTabStyles as styles } from './styles/categoriesTabStyles';
import { settingsStyles as st } from './styles/settingsStyles';
import { useCategoryReorder } from './useCategoryReorder';

interface ICategoriesTabProps {
	mailboxId: string;
}

interface ICategoryDraft {
	name: string;
	description: string;
	aiGuidance: string;
	color: string;
	isActive: boolean;
}

// Persisted per-category swatch data (not UI chrome), so it is a literal rather than a THEME token.
const DEFAULT_CATEGORY_COLOR = '#6B7280';

const EMPTY_CATEGORY_DRAFT: ICategoryDraft = {
	name: '',
	description: '',
	aiGuidance: '',
	color: DEFAULT_CATEGORY_COLOR,
	isActive: true,
};

export function CategoriesTab({ mailboxId }: ICategoriesTabProps) {
	const { data, isLoading, isError } = useSupportCategoriesQuery(mailboxId);
	const [categoryMutationError, setCategoryMutationError] = React.useState<string | null>(null);
	const createMutation = useCreateCategoryMutation(mailboxId, {
		onSuccess: () => setCategoryMutationError(null),
		onError: error => setCategoryMutationError(getApiErrorMessage(error, 'Failed to create category.')),
	});
	const updateMutation = useUpdateCategoryMutation(mailboxId, {
		onSuccess: () => setCategoryMutationError(null),
		onError: error => setCategoryMutationError(getApiErrorMessage(error, 'Failed to save category.')),
	});
	const deleteMutation = useDeleteCategoryMutation(mailboxId, {
		onSuccess: () => setCategoryMutationError(null),
		onError: error => setCategoryMutationError(getApiErrorMessage(error, 'Failed to delete category.')),
	});

	const [searchQuery, setSearchQuery] = React.useState('');
	const [expandedId, setExpandedId] = React.useState<string | null>(null);
	const [isCreating, setIsCreating] = React.useState(false);

	const [editDraft, setEditDraft] = React.useState<ICategoryDraft>(EMPTY_CATEGORY_DRAFT);
	const [createDraft, setCreateDraft] = React.useState<ICategoryDraft>(EMPTY_CATEGORY_DRAFT);
	const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);

	const serverCategories = [...(data?.values ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
	const {
		orderedCategories: allCategories,
		reorderError,
		getRowDragProps,
		dropIndicator,
	} = useCategoryReorder(serverCategories, updateMutation);

	const categories = searchQuery.trim()
		? allCategories.filter(
				c =>
					c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					(c.aiGuidance || '').toLowerCase().includes(searchQuery.toLowerCase())
			)
		: allCategories;

	const handleExpand = (category: ISupportCategory) => {
		if (expandedId === category.id) {
			setExpandedId(null);
			return;
		}
		setExpandedId(category.id);
		setEditDraft(getCategoryDraft(category));
	};

	const handleUpdate = () => {
		if (!expandedId || !editDraft.name.trim()) return;
		setCategoryMutationError(null);
		updateMutation.mutate(
			{
				id: expandedId,
				...getCategoryMutationPayload(editDraft),
				sortOrder: allCategories.find(c => c.id === expandedId)?.sortOrder ?? 0,
			},
			{
				onSuccess: () => {
					setExpandedId(null);
				},
			}
		);
	};

	const handleCreate = () => {
		if (!createDraft.name.trim()) return;
		setCategoryMutationError(null);
		createMutation.mutate(
			{
				...getCategoryMutationPayload(createDraft),
				sortOrder: allCategories.length,
			},
			{
				onSuccess: () => {
					setIsCreating(false);
					setCreateDraft(EMPTY_CATEGORY_DRAFT);
				},
			}
		);
	};

	const handleDelete = (id: string) => {
		setPendingDeleteId(id);
	};

	const confirmDelete = () => {
		if (!pendingDeleteId) return;
		setCategoryMutationError(null);
		deleteMutation.mutate(pendingDeleteId, {
			onSuccess: () => {
				if (expandedId === pendingDeleteId) setExpandedId(null);
				setPendingDeleteId(null);
			},
		});
	};

	const handleCancelCreate = () => {
		setIsCreating(false);
		setCreateDraft(EMPTY_CATEGORY_DRAFT);
	};

	const isFiltered = searchQuery.trim().length > 0;

	if (isLoading) return <LoadingSpinner />;
	if (isError) return <div className={css(st.error)}>Failed to load categories.</div>;

	return (
		<div>
			<h2 className={css(st.sectionTitle)}>Categories</h2>
			<div className={css(st.sectionDescription)}>
				The AI reads the guidance below to auto-categorize incoming tickets. Drag to reorder.
			</div>

			<div className={css(styles.toolbar)}>
				<Input
					type='text'
					value={searchQuery}
					onChange={e => setSearchQuery(e.target.value)}
					placeholder='Search categories…'
					styles={[st.input, styles.searchInput]}
				/>
				<Button onClick={() => setIsCreating(true)} disabled={isCreating} styles={[st.newButton]}>
					<PlusIcon />
					New category
				</Button>
			</div>

			{categoryMutationError && <div className={css(st.error)}>{categoryMutationError}</div>}

			{reorderError && (
				<div className={css(st.error)}>
					Reordering failed to save fully. The list has been refreshed — please try again.
				</div>
			)}

			{isCreating && (
				<div className={css(st.formCard)}>
					<h3 className={css(st.formTitle)}>New Category</h3>
					<CategoryForm
						draft={createDraft}
						onChange={patch => setCreateDraft(current => ({ ...current, ...patch }))}
						onCancel={handleCancelCreate}
						onSave={handleCreate}
						isSaving={createMutation.isPending}
						namePlaceholder='Enter category name...'
						descriptionPlaceholder='Brief description...'
						aiGuidancePlaceholder='Keywords or guidance for AI categorization...'
					/>
				</div>
			)}

			<div className={css(st.list)}>
				{categories.length === 0 && (
					<div className={css(st.emptyState)}>No categories yet. Create one to get started.</div>
				)}
				{categories.map(category => {
					const catIndex = allCategories.indexOf(category);
					const isDraggable = !isFiltered;
					const indicator = isDraggable ? dropIndicator(catIndex) : { above: false, below: false };
					return (
						<div
							key={category.id}
							className={css(
								styles.categoryItem,
								indicator.above && styles.categoryDropAbove,
								indicator.below && styles.categoryDropBelow
							)}
							{...(isDraggable ? getRowDragProps(catIndex) : {})}
						>
							<div
								className={css(styles.categoryHeader)}
								role='button'
								tabIndex={0}
								aria-expanded={expandedId === category.id}
								onClick={() => handleExpand(category)}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										handleExpand(category);
									}
								}}
							>
								{isDraggable && (
									<span className={css(styles.dragHandle)} title='Drag to reorder' aria-hidden>
										&#x2630;
									</span>
								)}
								<div className={css(styles.categoryInfo)}>
									<div className={css(styles.categoryNameRow)}>
										{category.color && (
											<span className={css(styles.colorSwatch)} style={{ backgroundColor: category.color }} />
										)}
										<span className={css(styles.categoryName)}>{category.name}</span>
										{category.aiGuidance && (
											<span className={css(styles.aiBadge)} title={category.aiGuidance}>
												AI
											</span>
										)}
										{!category.isActive && <span className={css(styles.inactiveBadge)}>Inactive</span>}
									</div>
									{category.description && (
										<span className={css(styles.categoryDescription)}>{category.description}</span>
									)}
								</div>
								<div className={css(styles.categoryActions)}>
									<Button
										variant='ghost'
										size='sm'
										onClick={e => {
											e.stopPropagation();
											handleDelete(category.id);
										}}
										title='Delete'
									>
										&times;
									</Button>
								</div>
							</div>

							{expandedId === category.id && (
								<div className={css(st.editForm)}>
									<CategoryForm
										draft={editDraft}
										onChange={patch => setEditDraft(current => ({ ...current, ...patch }))}
										onCancel={() => setExpandedId(null)}
										onSave={handleUpdate}
										isSaving={updateMutation.isPending}
									/>
								</div>
							)}
						</div>
					);
				})}
			</div>
			{pendingDeleteId && (
				<ConfirmModal
					title='Delete Category'
					message='Are you sure you want to delete this category? This action cannot be undone.'
					confirmLabel='Delete'
					variant='danger'
					onConfirm={confirmDelete}
					onCancel={() => setPendingDeleteId(null)}
				/>
			)}
		</div>
	);
}

function CategoryForm({
	aiGuidancePlaceholder,
	descriptionPlaceholder,
	draft,
	isSaving,
	namePlaceholder,
	onCancel,
	onChange,
	onSave,
}: {
	aiGuidancePlaceholder?: string;
	descriptionPlaceholder?: string;
	draft: ICategoryDraft;
	isSaving: boolean;
	namePlaceholder?: string;
	onCancel: () => void;
	onChange: (patch: Partial<ICategoryDraft>) => void;
	onSave: () => void;
}) {
	return (
		<>
			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Name</label>
				<Input
					type='text'
					value={draft.name}
					onChange={e => onChange({ name: e.target.value })}
					styles={[st.input]}
					placeholder={namePlaceholder}
				/>
			</div>
			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Description</label>
				<Input
					type='text'
					value={draft.description}
					onChange={e => onChange({ description: e.target.value })}
					styles={[st.input]}
					placeholder={descriptionPlaceholder}
				/>
			</div>
			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>AI Guidance / Keywords</label>
				<TextArea
					styles={[styles.textarea]}
					value={draft.aiGuidance}
					onChange={e => onChange({ aiGuidance: e.target.value })}
					placeholder={aiGuidancePlaceholder}
					rows={3}
				/>
			</div>
			<div className={css(styles.formRow)}>
				<div className={css(st.field, styles.formFieldSmall)}>
					<label className={css(st.fieldLabel)}>Color</label>
					<ColorPicker value={draft.color} onChange={color => onChange({ color })} />
				</div>
				<div className={css(st.field, styles.formFieldSmall)}>
					<label className={css(st.fieldLabel)}>Active</label>
					<div className={css(styles.checkboxRow)}>
						<Checkbox checked={draft.isActive} onCheckedChange={checked => onChange({ isActive: checked === true })} />
						<span className={css(styles.checkboxLabel)}>{draft.isActive ? 'Yes' : 'No'}</span>
					</div>
				</div>
			</div>
			<div className={css(st.formActionsTight)}>
				<Button onClick={onSave} disabled={isSaving || !draft.name.trim()}>
					{isSaving ? 'Saving...' : 'Save'}
				</Button>
				<Button onClick={onCancel}>Cancel</Button>
			</div>
		</>
	);
}

function getCategoryDraft(category: ISupportCategory): ICategoryDraft {
	return {
		name: category.name,
		description: category.description || '',
		aiGuidance: category.aiGuidance || '',
		color: category.color || EMPTY_CATEGORY_DRAFT.color,
		isActive: category.isActive,
	};
}

function getCategoryMutationPayload(draft: ICategoryDraft) {
	return {
		name: draft.name.trim(),
		description: draft.description.trim() || undefined,
		aiGuidance: draft.aiGuidance.trim() || undefined,
		color: draft.color,
		isActive: draft.isActive,
	};
}
